import { Set as ImmutableSet, type RecordOf as ImRecordOf, Record } from 'immutable';
import type { DateTime } from 'luxon';

import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';

import type { ChartOfAccountProps } from '../chart-of-accounts/chartOfAccountsTypes';
import { resourceStoreTypes } from '../core';
import type { Customer } from '../customers/customersTypes';
import type { Project } from '../projects/projectsTypes';

// ================================================================================================
// FilterStore - Filter Store Properties & Types
// ================================================================================================

export const FILTERS_REDUCER_KEY = 'filterStore';

export type FiltersStore = QcsSyncResourceStore<FilterProps>;

export const mkFiltersStore = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<FilterProps>();

// ================================================================================================
// FilterItems - FilterItems Store Properties & Types
// ================================================================================================
export type TagFilterItemProps = {
  type: 'TAG';
  tags: {
    id: string;
  }[];
};

export type PayeeFilterItemProps = {
  type: 'PAYEE';
  payees: string[];
};

export type ChartOfAccountFilterItemProps = {
  type: 'CHARTOFACCOUNT';
  chartOfAccounts: ChartOfAccountProps[];
};

export type AccountFilterItemProps = {
  type: 'ACCOUNT';
  accounts: { id: string }[];
};

export type UsageFilterItemProps = {
  type: 'USAGE';
  usages: { id: string; type: string }[];
};

export type CustomerProjectFilterItemProps = {
  type: 'CUSTOMERPROJECT';
  customerProjects: { customer: string; projects: string[] }[];
};

export type FilterItemProps =
  | TagFilterItemProps
  | PayeeFilterItemProps
  | ChartOfAccountFilterItemProps
  | AccountFilterItemProps
  | UsageFilterItemProps
  | CustomerProjectFilterItemProps;

// ================================================================================================
// QCS Filter - Filter Properties & Types
// ================================================================================================
export type FilterProps = {
  isDeleted?: boolean;
  type?: string;
  id?: string;
  parentIds?: string[];
  filterItems?: FilterItemProps[];
  clientId?: string;
};
export type Filter = ImRecordOf<FilterProps>;
export const mkFilter = Record<FilterProps>({
  id: undefined,
  isDeleted: undefined,
  type: undefined,
  parentIds: undefined,
  filterItems: undefined,
  clientId: undefined,
});

// ================================================================================================
// Simplifi Web Filter - Filter Properties & Types
// ================================================================================================
export type AdvancedFilter = {
  isBillOrSubscription?: boolean;
  isExcludedFromReports?: boolean;
  isReviewed?: boolean;
  isExcludedFromF2S?: boolean;
};
export const AdvancedFilterObject = Record<AdvancedFilter>({
  isBillOrSubscription: undefined,
  isExcludedFromReports: undefined,
  isReviewed: undefined,
  isExcludedFromF2S: undefined,
});
export type CustomerAndProjects = {
  customer: Customer;
  projects: Project[];
};

export type FilterObjectProps = {
  id?: string;
  categories?: ImmutableSet<string>;
  customersAndProjectsSelected?: ImmutableSet<CustomerAndProjects>;
  clientsAndProjects?: ImmutableSet<string>;
  tags?: ImmutableSet<string>;
  accounts?: ImmutableSet<string>;
  payees?: ImmutableSet<string>;
  payeeNames?: ImmutableSet<string>;
  flags?: ImmutableSet<string>;
  businesses?: ImmutableSet<string>;
  advanced?: ImRecordOf<AdvancedFilter>;
  numFilterItems?: number;
  filterOut?: boolean;
  dateStart?: DateTime | string | null;
  dateEnd?: DateTime | string | null;
};
export type FilterObject = ImRecordOf<FilterObjectProps>;
export const mkFilterObject = Record<FilterObjectProps>({
  id: undefined,
  categories: ImmutableSet(),
  customersAndProjectsSelected: ImmutableSet(),
  clientsAndProjects: ImmutableSet(),
  tags: ImmutableSet(),
  accounts: ImmutableSet(),
  payeeNames: ImmutableSet(),
  flags: ImmutableSet(),
  businesses: ImmutableSet(),
  advanced: AdvancedFilterObject(),
  numFilterItems: 0,
  filterOut: false,
  dateStart: null,
  dateEnd: null,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [FILTERS_REDUCER_KEY]: QcsSyncResourceStore<FilterProps>;
  }
}
